import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LADDER, MARKETS, SLOTS_PER_SET, type Track } from "../shared/domain.ts";
import {
  isCurrentWire,
  unpackTracks,
  type WireBoot,
  type WireCatalog,
} from "../shared/wire.ts";
import { DailySummary } from "./components/DailySummary.tsx";
import { GuessInput } from "./components/GuessInput.tsx";
import { GuessRows } from "./components/GuessRows.tsx";
import { LadderBar } from "./components/LadderBar.tsx";
import { RecentPlays } from "./components/RecentPlays.tsx";
import { SlotMeter } from "./components/SlotMeter.tsx";
import { SettingsButton } from "./components/Settings.tsx";
import { SongCard } from "./components/SongCard.tsx";
import { MixPicker } from "./components/MixPicker.tsx";
import { localDateKey, selectDailySet } from "./game/daily.ts";
import { CUSTOM_MIX_ID, mixById } from "./game/mixes.ts";
import { drawNext, filterKey, isOffered, tracksMatching, type Filter } from "./game/pools.ts";
import { currentDuration, skip, stepsSpent, submitGuess, type RoundState } from "./game/round.ts";
import { indexCatalog, type SearchableTrack } from "./game/search.ts";
import {
  completeDaily,
  currentStreak,
  isStale,
  loadState,
  recordUnlimitedPlay,
  resolveUnlimitedStreak,
  saveState,
  scoreOf,
  type DailyProgress,
  type SlotProgress,
  type StoredState,
} from "./game/storage.ts";
import { useClip } from "./game/useClip.ts";

type Mode = "daily" | "unlimited";

/** The whole preview — what you get once the Round is decided. */
const FULL_CLIP = LADDER[LADDER.length - 1]!;

function browserStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** A SlotProgress is a RoundState minus its answer, so this is a pure re-hydrate. */
function roundFor(track: Track, slot: SlotProgress | undefined): RoundState {
  return slot
    ? { answer: track, spent: slot.spent, status: slot.status }
    : { answer: track, spent: [], status: "playing" };
}

function toSlot(round: RoundState): SlotProgress {
  return { trackId: round.answer.id, spent: round.spent, status: round.status };
}

export function App() {
  /**
   * Two fetches, deliberately not one.
   *
   * `boot.json` (~22KB gz) carries the next ninety days of Daily Sets, so a Daily
   * Round is playable almost immediately. `songs.json` (~340KB gz) carries every
   * Track and is only needed for the Dropdown and for Unlimited — both of which
   * happen after the player has started listening. See scripts/pack.ts.
   */
  const [boot, setBoot] = useState<WireBoot | null>(null);
  const [catalog, setCatalog] = useState<SearchableTrack[] | null>(null);
  const [bootFailed, setBootFailed] = useState(false);
  const [songsFailed, setSongsFailed] = useState(false);
  const [state, setState] = useState<StoredState>(() => loadState(browserStorage()));
  const [mode, setMode] = useState<Mode>("daily");

  const today = useMemo(() => localDateKey(new Date()), []);

  useEffect(() => {
    saveState(browserStorage(), state);
  }, [state]);

  // Mirrors the `prefers-reduced-motion` switch in styles.css, so the in-app
  // setting and the OS setting go through the same rules.
  useEffect(() => {
    document.documentElement.dataset.motion = state.settings.reduceMotion ? "off" : "on";
  }, [state.settings.reduceMotion]);

  const setSettings = useCallback(
    (settings: StoredState["settings"]) => setState((prev) => ({ ...prev, settings })),
    [],
  );

  const settingsButton = (
    <SettingsButton
      values={state.settings}
      onChange={setSettings}
      info={{ trackCount: boot?.trackCount ?? catalog?.length ?? null, builtAt: boot?.builtAt ?? null }}
    />
  );

  useEffect(() => {
    let cancelled = false;

    const load = async <T,>(url: string): Promise<T> => {
      const res = await fetch(url);
      if (!res.ok) throw new Error(String(res.status));
      return (await res.json()) as T;
    };

    load<WireBoot>("/boot.json")
      .then((data) => {
        // A wire mismatch means a stale cached file from an older build. Treated
        // as a miss so the full song list can still carry the day.
        if (!cancelled) (isCurrentWire(data) ? setBoot : setBootFailed)(data as never);
      })
      .catch(() => !cancelled && setBootFailed(true));

    load<WireCatalog>("/songs.json")
      .then((data) => {
        if (cancelled) return;
        if (!isCurrentWire(data)) return setSongsFailed(true);
        setCatalog(indexCatalog(unpackTracks(data)));
      })
      .catch(() => !cancelled && setSongsFailed(true));

    return () => {
      cancelled = true;
    };
  }, []);

  // Only fatal once *both* have failed: either one alone still leaves a playable
  // game, just a narrower one.
  if (bootFailed && songsFailed) {
    return (
      <Shell>
        <p className="banner" role="status">
          Couldn't load the song catalog. Try again in a moment.
        </p>
      </Shell>
    );
  }

  if (!boot && !catalog) {
    return (
      <Shell>
        <p className="status">Loading…</p>
      </Shell>
    );
  }

  return (
    <Shell action={settingsButton}>
      <nav className="tabs" role="tablist">
        {(["daily", "unlimited"] as const).map((m) => (
          <button
            key={m}
            role="tab"
            aria-selected={mode === m}
            className={`tab${mode === m ? " tab--on" : ""}`}
            onClick={() => setMode(m)}
            type="button"
          >
            {m === "daily" ? "Daily" : "Unlimited"}
          </button>
        ))}
      </nav>

      {mode === "daily" ? (
        <Daily
          catalog={catalog}
          boot={boot}
          today={today}
          state={state}
          setState={setState}
        />
      ) : catalog ? (
        <Unlimited catalog={catalog} state={state} setState={setState} />
      ) : (
        // Unlimited has nothing to draw from until the song list lands. Daily
        // does, which is the whole point of the split.
        <p className="status">Loading the song list…</p>
      )}

      <footer className="foot">
        Songs licensed for {MARKETS.join(", ")}. Elsewhere the catalog may be patchy — wider
        coverage is planned.
      </footer>
    </Shell>
  );
}

function Shell({ children, action }: { children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <main className="shell">
      <header className="head">
        <h1 className="wordmark">Spotiguessr</h1>
        {action && <div className="head__action">{action}</div>}
      </header>
      {children}
    </main>
  );
}

function Daily({
  catalog,
  boot,
  today,
  state,
  setState,
}: {
  /** Null until the song list lands. Daily plays without it; the Dropdown cannot. */
  catalog: SearchableTrack[] | null;
  boot: WireBoot | null;
  today: string;
  state: StoredState;
  setState: React.Dispatch<React.SetStateAction<StoredState>>;
}) {
  /**
   * Today's five Tracks, from the baked Set when the build covers today.
   *
   * The fallback recomputes from the full song list, which is what always
   * happened before and still happens once a build is older than boot.json's
   * ninety-day window. Both paths run the same `selectDailySet` over the same
   * Catalog, so they agree — `pack.test.ts` holds them to it.
   */
  const volume = state.settings.volume;

  const tracks = useMemo(() => {
    const baked = boot?.days?.[today];
    if (baked) return indexCatalog(unpackTracks(baked));
    return catalog ? selectDailySet(catalog, today) : null;
  }, [boot, catalog, today]);

  // A finished Slot is held on screen until the player moves on. Without this
  // the next Slot would load the instant they solved one, and they'd never see
  // what the song actually was.
  const [reviewing, setReviewing] = useState<RoundState | null>(null);

  // A Set in progress is locked to its date, so changing the clock cannot
  // re-roll a bad start — yesterday's unfinished Set is simply gone.
  const progress: DailyProgress =
    state.current && !isStale(state.current, today)
      ? state.current
      : { date: today, slots: [], completed: false };

  const activeIndex = useMemo(() => {
    const playing = progress.slots.findIndex((s) => s.status === "playing");
    if (playing >= 0) return playing;
    return progress.slots.length < SLOTS_PER_SET ? progress.slots.length : -1;
  }, [progress.slots]);

  const done = activeIndex === -1;
  const activeTrack = done || !tracks ? null : (tracks[activeIndex] ?? null);
  const round = activeTrack ? roundFor(activeTrack, progress.slots[activeIndex]) : null;

  // While reviewing, keep the *reviewed* Slot's audio loaded. `activeIndex` has
  // already advanced by then, so following it here would load the next Slot and
  // let the replay button spoil the song the player hasn't reached yet.
  const { ready, failure, play, retry } = useClip(
    reviewing?.answer.id ?? activeTrack?.id ?? null,
    volume,
  );

  const commit = useCallback(
    (next: RoundState) => {
      if (next.status !== "playing") setReviewing(next);

      setState((prev) => {
        // Derived inside the updater so this never writes a stale snapshot.
        const base =
          prev.current && prev.current.date === today
            ? prev.current
            : { date: today, slots: [], completed: false };

        const slots = [...base.slots];
        slots[activeIndex] = toSlot(next);
        const updated: DailyProgress = { ...base, slots };

        return slots.length === SLOTS_PER_SET && slots.every((s) => s.status !== "playing")
          ? completeDaily(prev, updated)
          : { ...prev, current: updated };
      });
    },
    [activeIndex, setState, today],
  );

  if (reviewing) {
    return (
      <>
        <SlotMeter index={progress.slots.length - 1} played={progress.slots.length - 1} />
        <Board
          round={reviewing}
          catalog={catalog ?? []}
          ready={ready}
          failure={failure}
          play={play}
          onRetry={retry}
          onChange={() => undefined}
          onNext={() => setReviewing(null)}
          nextLabel={progress.slots.length >= SLOTS_PER_SET ? "See results" : "Next song"}
        />
      </>
    );
  }

  if (done) {
    const result = state.history.find((h) => h.date === today) ?? {
      date: today,
      score: scoreOf(progress.slots),
      verdicts: progress.slots.map((s) => s.spent.map((x) => x.verdict)),
    };
    return (
      <DailySummary
        result={result}
        streak={currentStreak(state, today)}
        answers={(tracks ?? []).map((t) => ({ artist: t.artist, title: t.title }))}
      />
    );
  }

  if (!tracks) return <p className="status">Loading the song list…</p>;
  if (!round) return <p className="status">Not enough songs for today's set yet.</p>;

  return (
    <>
      <SlotMeter index={activeIndex} played={activeIndex} />
      {currentStreak(state, today) > 0 && (
        <p className="slotline slotline--streak">
          <span className="slotline__streak">🔥 {currentStreak(state, today)} day streak</span>
        </p>
      )}
      <Board
        round={round}
        catalog={catalog ?? []}
        ready={ready}
        failure={failure}
        play={play}
        onRetry={retry}
        onChange={commit}
      />
    </>
  );
}

function Unlimited({
  catalog,
  state,
  setState,
}: {
  catalog: SearchableTrack[];
  state: StoredState;
  setState: React.Dispatch<React.SetStateAction<StoredState>>;
}) {
  const [round, setRound] = useState<RoundState | null>(null);
  // The Change path. Kept local so cancelling puts the player back into the
  // Round they were already halfway through, rather than discarding it.
  const [picking, setPicking] = useState(false);

  const volume = state.settings.volume;

  const { mixId, custom, streak, seen } = state.unlimited;
  const mix = mixId && mixId !== CUSTOM_MIX_ID ? mixById(mixId) : null;
  const filter: Filter | null = mixId === CUSTOM_MIX_ID ? custom : (mix?.filter ?? null);
  const name = mixId === CUSTOM_MIX_ID ? "Custom mix" : (mix?.name ?? null);

  const key = filter ? filterKey(filter) : "";
  const pool = useMemo(() => (filter ? tracksMatching(catalog, filter) : []), [catalog, filter]);
  // Shown in the header so Unlimited has the same sense of progress the Mix cards
  // give — a Round in Unlimited otherwise tells the player nothing about where
  // they are.
  const heardHere = (seen[key] ?? []).length;
  // The floor is enforced here, not just in the picker: a Filter restored from
  // stale state, or a Mix since removed from the lineup, must land the player
  // back on the setup screen rather than on a Board that cannot draw.
  const playable = filter !== null && isOffered(catalog, filter);

  // Which Round's outcome has already been counted toward the Streak, so a
  // re-render cannot score the same Round twice.
  const scored = useRef<number | null>(null);

  const start = useCallback(() => {
    if (!filter) return;
    const { track, exhausted } = drawNext(pool, new Set(seen[key] ?? []));
    if (!track) return;

    scored.current = null;
    setRound({ answer: track, spent: [], status: "playing" });
    setState((prev) => ({
      ...prev,
      unlimited: {
        ...prev.unlimited,
        // Exhausting the Pool starts a fresh cycle rather than a dead end.
        seen: {
          ...prev.unlimited.seen,
          [key]: exhausted ? [track.id] : [...(prev.unlimited.seen[key] ?? []), track.id],
        },
      },
    }));
  }, [filter, pool, seen, key, setState]);

  const choose = useCallback(
    (nextId: string, nextCustom?: Filter) => {
      setRound(null);
      setPicking(false);
      setState((prev) => ({
        ...prev,
        unlimited: {
          ...prev.unlimited,
          mixId: nextId,
          custom: nextCustom ?? prev.unlimited.custom,
        },
      }));
    },
    [setState],
  );

  useEffect(() => {
    if (!round && playable && !picking) start();
    // `start` intentionally omitted: it changes on every seen-set update, and
    // re-running here would draw a second track mid-Round.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round, playable, picking]);

  useEffect(() => {
    if (!round || round.status === "playing") return;
    if (scored.current === round.answer.id) return;
    scored.current = round.answer.id;

    // The same guard covers the streak and the history: both must happen exactly
    // once per finished Round, and the Round object stays mounted until the
    // player asks for the next song.
    setState((prev) =>
      recordUnlimitedPlay(
        {
          ...prev,
          unlimited: {
            ...prev.unlimited,
            streak: resolveUnlimitedStreak(prev.unlimited.streak, round.status),
          },
        },
        {
          trackId: round.answer.id,
          artist: round.answer.artist,
          title: round.answer.title,
          artworkUrl: round.answer.artworkUrl,
          verdicts: round.spent.map((s) => s.verdict),
          status: round.status === "won" ? "won" : "revealed",
        },
      ),
    );
  }, [round, setState]);

  const { ready, failure, play, retry } = useClip(round?.answer.id ?? null, volume);

  if (picking || !playable) {
    return (
      <MixPicker
        tracks={catalog}
        seen={seen}
        custom={custom}
        onChoose={choose}
        onCancel={picking && round ? () => setPicking(false) : undefined}
      />
    );
  }

  return (
    <>
      <div className="mix-bar">
        <div className="mix-bar__row">
          <p className="mix-bar__name">{name}</p>
          <button className="mix-bar__change" type="button" onClick={() => setPicking(true)}>
            Change
          </button>
        </div>
        <p className="mix-bar__row">
          {streak > 0 && (
            <>
              <span className="mix-bar__streak">🔥 {streak} in a row</span>
              <span aria-hidden="true">·</span>
            </>
          )}
          <span className="mix-bar__heard">
            heard {heardHere} of {pool.length}
          </span>
        </p>
      </div>

      <div className="withpanel">
        <div className="withpanel__main stack">
          {round ? (
            <Board
              round={round}
              catalog={catalog}
              ready={ready}
              failure={failure}
              play={play}
              onRetry={retry}
              onChange={setRound}
              onNext={start}
            />
          ) : (
            <p className="status">Loading the next song…</p>
          )}
        </div>

        <RecentPlays plays={state.unlimited.recent} />
      </div>
    </>
  );
}

function Board({
  round,
  catalog,
  ready,
  failure,
  play,
  onRetry,
  onChange,
  onNext,
  nextLabel = "Next song",
}: {
  round: RoundState;
  catalog: SearchableTrack[];
  ready: boolean;
  failure: { kind: "quota" | "other"; message: string } | null;
  play: (seconds: number, startSeconds?: number) => Promise<void>;
  onRetry?: () => void;
  onChange: (next: RoundState) => void;
  onNext?: () => void;
  nextLabel?: string;
}) {
  const titleFor = useMemo(() => {
    const byId = new Map(catalog.map((t) => [t.id, t]));
    return (id: number) => {
      const t = byId.get(id);
      return t ? `${t.artist} — ${t.title}` : "Unknown track";
    };
  }, [catalog]);

  const playing = round.status === "playing";

  // Once the answer is out, the Ladder stops mattering — the reward for solving
  // it is hearing the whole thing.
  const audible = playing ? currentDuration(round) : FULL_CLIP;

  // Every Step starts at the hook, so each rung contains the one before it. The
  // reward play is the exception: it starts at 0 and runs the whole preview,
  // which still contains everything the player already heard.
  const from = playing ? round.answer.hookStart : 0;

  const celebrated = useRef<number | null>(null);
  useEffect(() => {
    if (playing || !ready) return;
    // Guard so re-renders don't restart the song; the AudioContext is already
    // unlocked by the gesture that ended the Round, so this needs no click.
    if (celebrated.current === round.answer.id) return;
    celebrated.current = round.answer.id;
    void play(FULL_CLIP, 0);
  }, [playing, ready, round.answer.id, play]);

  return (
    <>
      {failure && (
        <div className={`banner${failure.kind === "quota" ? " banner--quota" : ""}`} role="status">
          <p className="banner__text">{failure.message}</p>
          {onRetry && (
            <button className="banner__retry" type="button" onClick={onRetry}>
              Try again
            </button>
          )}
        </div>
      )}

      <GuessRows spent={round.spent} titleFor={titleFor} />
      <LadderBar stepsSpent={stepsSpent(round)} />

      <div className="play">
        <button
          className="play__btn"
          type="button"
          disabled={!ready}
          onClick={() => void play(audible, from)}
          aria-label={playing ? `Play ${audible} seconds` : "Play the full song"}
        >
          {ready ? "▶" : "…"}
        </button>
      </div>

      {playing ? (
        <GuessInput
          catalog={catalog}
          disabled={!ready}
          onGuess={(track) => onChange(submitGuess(round, track))}
          onSkip={() => onChange(skip(round))}
        />
      ) : (
        <div className="outcome">
          <SongCard
            track={round.answer}
            outcome={round.status === "won" ? "won" : "revealed"}
            steps={stepsSpent(round)}
          />
          {onNext ? (
            <button className="btn btn--primary" type="button" onClick={onNext}>
              {nextLabel}
            </button>
          ) : (
            <p className="status">Loading the next song…</p>
          )}
        </div>
      )}
    </>
  );
}
